// BILLING-001 Adyvance Wallet (owner, 2026-10-25; D-064). One prepaid NPR balance per business
// (1 credit = Rs 1) that pays for everything Adyvance sells: plans, SMS credits, root AI credits,
// premium themes, Digital Marketing ads, Import Marketplace payments and add-ons. Shops add money by
// QR or bank transfer with the receipt and Adyvance confirms it in the owner console. The balance is
// non-refundable and non-transferable. Shared by the admin, the console, the mocks and the server.
import type { AdsCampaignRequest, AdsPlatform } from "./ads-service";
import type { PlanIcon } from "./plan-features";
import type { SmsPayDetails } from "./sms-service";

/**
 * How a shop adds money: QR or bank transfer with the receipt, or Fonepay's own payment page (hosted
 * checkout, D-122) once the owner sets them up. Owner, 2026-11-05: shops pay Adyvance only by bank,
 * QR or Fonepay checkout; Fonepay Dynamic QR (D-085) was dropped (eSewa and Khalti were earlier).
 */
export const WALLET_TOPUP_METHODS = ["qr", "bank", "fonepay_web"] as const;
export type WalletTopupMethod = (typeof WALLET_TOPUP_METHODS)[number];
/** Online methods: paid through Adyvance's own merchant accounts (console → Payment methods). */
export const WALLET_ONLINE_METHODS: readonly WalletTopupMethod[] = ["fonepay_web"];

// ---------- online payments to Adyvance (D-084) ----------

/**
 * The gateway a shop can pay Adyvance with online: Fonepay hosted checkout (`fonepay_web`, D-122: the
 * shop is sent to Fonepay's page and comes back). Fonepay Dynamic QR (`fonepay`) was dropped on
 * 2026-11-05; payments it already made still settle (system/ady-pay.ts).
 */
export const ADY_GATEWAYS = ["fonepay_web"] as const;
export type AdyGateway = (typeof ADY_GATEWAYS)[number];

/** The secret fields each gateway needs (names only; values never leave the server). */
export const ADY_GATEWAY_FIELDS: Readonly<Record<AdyGateway, readonly string[]>> = {
  fonepay_web: ["merchant_code", "secret_key"],
};

/** One gateway as the owner console shows it: switches and last-4 of each field, never a value. */
export interface AdyGatewayView {
  provider: AdyGateway;
  enabled: boolean;
  /** What the owner chose. Local always pays in test mode whatever this says. */
  mode: "test" | "live";
  fields: { field: string; last4: string; updated_at: string | null }[];
  /** Every field the gateway needs is saved. */
  configured: boolean;
  updated_at: string | null;
}

/**
 * What a shop pays for online. `topup` adds the money to the wallet; the others add it and then buy
 * the item from the wallet at once (a "direct" payment). If the purchase can't go through after the
 * payment, the money stays in the wallet.
 */
export type AdyPayIntent =
  | { kind: "topup" }
  | {
      kind: "plan";
      plan_id: string;
      period: PlanPeriod;
      auto_renew: boolean;
      coupon_code?: string;
    }
  | { kind: "addon"; offer_key: string; quantity: number }
  | { kind: "ai_pack"; pack_id: string }
  | { kind: "theme"; theme_key: string }
  | { kind: "sms_pack"; pack_id: string }
  | { kind: "import_payment"; order_id: string; amount_paisa: number }
  /** D-086: a Digital Marketing ad; paid, it is requested from the wallet at once. */
  | { kind: "ads_campaign"; store_id: string; platform: AdsPlatform; ad: AdsCampaignRequest };
export type AdyPayKind = AdyPayIntent["kind"];

/** One online payment as the shop (and the console) sees it. */
export interface AdyPayment {
  id: string;
  org_id: string;
  org_name?: string;
  provider: AdyGateway;
  mode: "test" | "live";
  amount_paisa: number;
  kind: AdyPayKind;
  state: "pending" | "paid" | "failed" | "expired" | "cancelled";
  /** After a paid payment: credited (top-up), bought (direct), kept_in_wallet (purchase failed). */
  outcome: "credited" | "bought" | "kept_in_wallet" | null;
  /** Why it failed or why the money stayed in the wallet (plain words). */
  note: string | null;
  provider_txn_id: string | null;
  created_at: string | null;
  decided_at: string | null;
}

/**
 * What the shop does next: scan Fonepay's QR for the exact amount, good until `expires_at` (5 minutes),
 * or (hosted checkout, D-122) go to Fonepay's page at `url`; Fonepay sends the browser back to the
 * admin, which shows the result.
 */
export type AdyPayNext =
  { kind: "qr"; qr: string; expires_at: string } | { kind: "redirect"; url: string };

export interface AdyPayStart {
  payment: AdyPayment;
  next: AdyPayNext;
}

/** Where wallet money went, grouped for the shop's spending view. */
export const WALLET_CATEGORIES = [
  "plan",
  "sms",
  "ai",
  "theme",
  "ads",
  "import",
  "addon",
  "messages",
  "topup",
  "bonus",
  "adjustment",
] as const;
export type WalletCategory = (typeof WALLET_CATEGORIES)[number];

/** `wallet_ledger.ref_type` → category. Unknown types count as an adjustment. */
const CATEGORY_OF_REF: Record<string, WalletCategory> = {
  subscription: "plan",
  sms_pack: "sms",
  ai_pack: "ai",
  theme: "theme",
  ads_campaign: "ads",
  ads_wallet: "ads",
  import_payment: "import",
  addon: "addon",
  outbound_message: "messages",
  campaign: "messages",
  wallet_topup: "topup",
  topup: "topup",
  topup_bonus: "bonus",
  // D-067: referral rewards are credit Adyvance gives, like a top-up bonus.
  referral: "bonus",
  adjustment: "adjustment",
};
export const walletCategoryOf = (refType: string): WalletCategory =>
  CATEGORY_OF_REF[refType] ?? "adjustment";

/** Plan periods a shop can buy, and how many months each covers. */
export const PLAN_PERIODS = ["monthly", "quarterly", "yearly"] as const;
export type PlanPeriod = (typeof PLAN_PERIODS)[number];
export const PLAN_PERIOD_MONTHS: Record<PlanPeriod, number> = {
  monthly: 1,
  quarterly: 3,
  yearly: 12,
};

/** Error code when the balance can't cover a purchase (409, with needed/available/short paisa). */
export const WALLET_SHORT_CODE = "billing.insufficient_balance";
export interface WalletShortDetails {
  needed_paisa: number;
  available_paisa: number;
  short_paisa: number;
}

/**
 * Where shops pay Adyvance by QR or bank transfer (console → Payment methods, D-086): the one set of
 * details every service uses. A method that is off, or not filled in, is not shown to shops.
 */
export interface WalletPayDetails extends SmsPayDetails {
  qr_enabled: boolean;
  bank_enabled: boolean;
}

export interface WalletTopupSettings {
  /** Amount buttons in the top-up popup, paisa. */
  presets: number[];
  min_paisa: number;
  max_paisa: number;
}

/** Top up at least `min_paisa` and get `percent` % extra (the highest tier that fits applies). */
export interface WalletBonusTier {
  min_paisa: number;
  percent: number;
}

export interface WalletTopup {
  id: string;
  org_id: string;
  org_name?: string;
  amount_paisa: number;
  bonus_paisa: number;
  method: WalletTopupMethod;
  reference: string | null;
  /** Payment receipt (an Import Marketplace file ref, o_imf_…). */
  proof: string | null;
  state: "submitted" | "confirmed" | "rejected" | "cancelled";
  credited_paisa: number | null;
  note: string | null;
  staff_note: string | null;
  created_at: string | null;
  decided_at: string | null;
  /** Console only: other top-ups with the same transaction ID (any business), not rejected. */
  duplicates?: { id: string; org_name: string; state: string }[];
}

/** One row of the shop's wallet activity (the statement, in plain words). */
export interface WalletActivity {
  id: string;
  kind: "topup" | "debit" | "reserve" | "release" | "refund" | "mission";
  direction: "in" | "out" | "hold";
  amount_paisa: number;
  category: WalletCategory;
  ref_type: string;
  ref_id: string;
  /** What it was for ("Growth plan · 3 months", "SMS pack · 1,000 credits"). */
  note: string | null;
  balance_after_paisa: number;
  created_at: string | null;
}

export interface WalletUpcoming {
  kind: "renewal" | "import";
  title: string;
  amount_paisa: number;
  due_at: string | null;
  /** Renewal only: paid automatically from the wallet on the due date. */
  auto: boolean;
  /** The available balance covers it (together with the items above it). */
  covered: boolean;
  /** Admin path below /{org}/ to act on it. */
  link: string;
}

/** Hints computed from the wallet and what the shop uses. */
export type WalletTipKind =
  | "low_balance"
  | "renewal_short"
  | "auto_renew_off"
  | "bonus_next"
  | "pending_topup"
  | "import_due";
export interface WalletTip {
  kind: WalletTipKind;
  params: Record<string, string | number>;
}

export interface WalletServices {
  plan: {
    key: string;
    name: string;
    kind: "base" | "trial" | "paid";
    state: string;
    ends_at: string | null;
    period: string;
    auto_renew: boolean;
    renew_price_paisa: number | null;
  };
  sms: { credits: number };
  ai: { extra_credits: number; month_used: number; month_limit: number | null };
  themes: { premium: number; owned: number };
  ads: { live: number; in_review: number };
  import: { due_paisa: number; orders_due: number };
}

export interface WalletOverview {
  wallet: {
    currency: "NPR";
    balance_paisa: number;
    reserved_paisa: number;
    available_paisa: number;
    low_threshold_paisa: number | null;
    low: boolean;
  };
  topup: {
    settings: WalletTopupSettings;
    bonus: WalletBonusTier[];
    pay: WalletPayDetails;
    /** Methods that work now (QR and bank always; online ones once the owner turns them on). */
    methods: Record<WalletTopupMethod, boolean>;
    /** Online gateways the shop can pay with now (D-084), in display order. */
    online: AdyGateway[];
  };
  topups: WalletTopup[];
  /** Asked for and waiting for Adyvance to check. */
  pending_paisa: number;
  insights: {
    month: {
      added_paisa: number;
      spent_paisa: number;
      by_category: { category: WalletCategory; paisa: number }[];
    };
    /** Last six NPT months, oldest first. */
    months: { month: string; added_paisa: number; spent_paisa: number }[];
    /** Average net spend per day over the last 30 days. */
    daily_spend_paisa: number;
    /** How many days the available balance lasts at that pace (null: no recent spend). */
    runway_days: number | null;
    /** What to add to cover what is due in 30 days plus 30 days at the current pace. */
    suggested_topup_paisa: number | null;
    upcoming: WalletUpcoming[];
    tips: WalletTip[];
  };
  services: WalletServices;
  rules: { refundable: false; transferable: false };
}

export interface WalletPlanOffer {
  id: string;
  key: string;
  name: string;
  /** The icon the owner gave the plan (D-065). */
  icon: PlanIcon;
  summary: string | null;
  badge: string | null;
  prices: { period: PlanPeriod; amount_paisa: number; monthly_paisa: number; save_pct: number }[];
  trial_days: number | null;
  current: boolean;
  entitlements: { key: string; kind: "flag" | "quota"; value: number }[];
}

export interface WalletPlans {
  plans: WalletPlanOffer[];
  current: WalletServices["plan"] & { plan_id: string; starts_at: string | null };
  available_paisa: number;
  /** Credit the shop gets back for the unused part of its current paid period on a plan change. */
  unused_credit_paisa: number;
}

export interface AiCreditPack {
  id: string;
  credits: number;
  price_paisa: number;
  active: boolean;
}

export interface AiCreditAccount {
  balance: number;
  purchased: number;
  used: number;
  packs: AiCreditPack[];
  month: { used: number; limit: number | null };
  ledger: {
    id: string;
    kind: "purchase" | "grant" | "debit" | "refund";
    credits: number;
    ref_type: string;
    balance_after: number;
    note: string | null;
    created_at: string | null;
  }[];
}

export interface PremiumTheme {
  key: string;
  name: string;
  price_paisa: number;
  period: "once" | "yearly";
  owned: boolean;
  ends_at: string | null;
}

export interface AddonOfferView {
  key: string;
  quota_key: string;
  increment: number;
  /** Price for one month of one add-on; null = not on sale. */
  monthly_paisa: number | null;
}

// ---------- owner console ----------

export interface WalletRow {
  org_id: string;
  org_name: string;
  balance_paisa: number;
  reserved_paisa: number;
  spent_30d_paisa: number;
  last_at: string | null;
}

export interface WalletBoard {
  summary: {
    /** Money Adyvance holds for shops (sum of balances). */
    held_paisa: number;
    wallets: number;
    topups_waiting: number;
    waiting_paisa: number;
    added_month_paisa: number;
    spent_month_paisa: number;
    by_category: { category: WalletCategory; paisa: number }[];
  };
  topups: WalletTopup[];
  wallets: WalletRow[];
  settings: {
    topup: WalletTopupSettings;
    bonus: WalletBonusTier[];
    pay: WalletPayDetails;
    low_threshold_paisa: number | null;
  };
  /** Online payment gateways (D-084): switches and last-4 only. */
  gateways: AdyGatewayView[];
  /** The latest online payments from every shop (newest first). */
  online_payments: AdyPayment[];
  ai_packs: AiCreditPack[];
  themes: {
    key: string;
    name: string;
    price_paisa: number | null;
    period: "once" | "yearly";
    active: boolean;
    owners: number;
  }[];
  addons: AddonOfferView[];
}
